import React, { useState } from 'react';
import axios from 'axios';
import Modal from '../ui/Modal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventModal = ({ event, selectedDate, onClose, onSave, language, t }) => {
  const [formData, setFormData] = useState({
    title: event?.title || '',
    event_type: event?.event_type || 'other',
    date: event?.date ? new Date(event.date).toISOString().slice(0, 16) : 
          selectedDate ? `${selectedDate}T12:00` : new Date().toISOString().slice(0, 16),
    end_date: event?.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : '',
    description: event?.description || '',
    notes: event?.notes || '',
    reminder_days: event?.reminder_days || null,
    is_recurring: event?.is_recurring || false,
    recurrence_pattern: event?.recurrence_pattern || 'weekly',
    recurrence_days: event?.recurrence_days || [],
    recurrence_end_date: event?.recurrence_end_date ? new Date(event.recurrence_end_date).toISOString().slice(0, 10) : '',
    training_duration: event?.training_duration || '',
    training_notes: event?.training_notes || '',
    training_completed: event?.training_completed || false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const submitData = {
        ...formData,
        end_date: formData.end_date || null,
        reminder_days: formData.reminder_days ? parseInt(formData.reminder_days) : null,
        training_duration: formData.training_duration ? parseInt(formData.training_duration) : null
      };

      if (submitData.is_recurring && (!submitData.recurrence_days || submitData.recurrence_days.length === 0)) {
        alert('Por favor selecciona al menos un día para el evento recurrente');
        setSaving(false);
        return;
      }

      if (event) {
        await axios.put(`${API}/events/${event.id}`, submitData);
      } else {
        await axios.post(`${API}/events`, submitData);
      }
      
      onSave();
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Error al guardar el evento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-2xl">
      <Modal.Header onClose={onClose}>
        <h2 className="text-2xl font-bold text-white">
          {event ? 'Editar evento' : 'Crear evento'}
        </h2>
      </Modal.Header>

      <Modal.Body>
        <form id="event-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-blue-200 text-sm font-medium mb-2">Título *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="input-field w-full"
              required
              placeholder="ej., Entrenamiento de maratón, Curso de yoga"
            />
          </div>

          <div>
            <label className="block text-blue-200 text-sm font-medium mb-2">Tipo de actividad *</label>
            <select
              value={formData.event_type}
              onChange={(e) => setFormData({...formData, event_type: e.target.value})}
              className="input-field w-full"
              required
            >
              <option value="walking">Caminar (Cardio)</option>
              <option value="running">Running</option>
              <option value="cycling">Ciclismo</option>
              <option value="trail_running">Trail Running</option>
              <option value="martial_arts">Artes marciales</option>
              <option value="acrobatics">Acrobacias</option>
              <option value="swimming">Natación</option>
              <option value="yoga">Yoga</option>
              <option value="pilates">Pilates</option>
              <option value="crossfit">CrossFit</option>
              <option value="hiking">Senderismo</option>
              <option value="climbing">Escalada</option>
              <option value="rowing">Remo</option>
              <option value="boxing">Boxeo</option>
              <option value="dance">Baile</option>
              <option value="other">Otro</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2">Fecha y hora de inicio *</label>
              <input
                type="datetime-local"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="input-field w-full"
                required
                disabled={event && event.parent_event_id}
              />
            </div>
            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2">Fecha y hora de fin</label>
              <input
                type="datetime-local"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                className="input-field w-full"
              />
            </div>
          </div>

          {!event && (
            <div className="border-t border-blue-400/20 pt-4">
              <label className="flex items-center gap-2 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={formData.is_recurring}
                  onChange={(e) => setFormData({...formData, is_recurring: e.target.checked})}
                  className="w-4 h-4 text-blue-500 rounded"
                />
                <span className="text-blue-200 font-medium">🔁 Evento recurrente (semanal)</span>
              </label>

              {formData.is_recurring && (
                <div className="space-y-4 bg-blue-900/20 p-4 rounded-lg">
                  <div>
                    <label className="block text-blue-200 text-sm font-medium mb-2">Repetir en:</label>
                    <div className="grid grid-cols-7 gap-2">
                      {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((day, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            const days = formData.recurrence_days || [];
                            if (days.includes(index)) {
                              setFormData({...formData, recurrence_days: days.filter(d => d !== index)});
                            } else {
                              setFormData({...formData, recurrence_days: [...days, index]});
                            }
                          }}
                          className={`px-2 py-2 rounded text-xs font-medium ${
                            formData.recurrence_days?.includes(index)
                              ? 'bg-blue-500 text-white'
                              : 'bg-blue-900/30 text-blue-300 hover:bg-blue-800/40'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-blue-200 text-sm font-medium mb-2">Repetir hasta:</label>
                    <input
                      type="date"
                      value={formData.recurrence_end_date}
                      onChange={(e) => setFormData({...formData, recurrence_end_date: e.target.value})}
                      className="input-field w-full"
                    />
                    <p className="text-blue-300 text-xs mt-1">Dejar vacío para repetir durante 1 año</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {event && new Date(event.date) <= new Date() && (
            <div className="border-t border-green-400/20 pt-4">
              <h3 className="text-blue-200 font-medium mb-3">📊 Datos de la sesión de entrenamiento</h3>
              <div className="space-y-3 bg-green-900/10 p-4 rounded-lg">
                <div>
                  <label className="block text-blue-200 text-sm font-medium mb-2">Duración (minutos)</label>
                  <input
                    type="number"
                    value={formData.training_duration}
                    onChange={(e) => setFormData({...formData, training_duration: e.target.value})}
                    className="input-field w-full"
                    placeholder="ej., 45"
                  />
                </div>
                <div>
                  <label className="block text-blue-200 text-sm font-medium mb-2">Notas del entrenamiento</label>
                  <textarea
                    value={formData.training_notes}
                    onChange={(e) => setFormData({...formData, training_notes: e.target.value})}
                    className="input-field w-full"
                    rows="2"
                    placeholder="¿Cómo fue? ¿Alguna observación?"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.training_completed}
                    onChange={(e) => setFormData({...formData, training_completed: e.target.checked})}
                    className="w-4 h-4 text-green-500 rounded"
                  />
                  <span className="text-green-200 font-medium">✅ Entrenamiento completado</span>
                </label>
              </div>
            </div>
          )}

          <div>
            <label className="block text-blue-200 text-sm font-medium mb-2">Descripción</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="input-field w-full"
              rows="3"
              placeholder="¿De qué trata este evento?"
            />
          </div>

          <div>
            <label className="block text-blue-200 text-sm font-medium mb-2">Notas personales</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="input-field w-full"
              rows="2"
              placeholder="Recordatorios de preparación, objetivos, etc."
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button type="submit" form="event-form" disabled={saving} className="btn-primary flex-1">
              {saving ? '⏳ Guardando...' : '💾 Guardar'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancelar
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default EventModal;
